import React from 'react';

/* Designed SVG glyphs — Velvet Chamber Gen-2.0.
   Ported from the design package (vc-entry.jsx, vc-constellation.jsx,
   directions/g2-components.jsx). No emoji anywhere in the flow. */

// ── VCMeshGlyph — brand mark (FAB, shells) ──────────────────────────────────
export const VCMeshGlyph = ({ stroke = 'var(--fg-on-brand)' }: { stroke?: string }) => (
  <svg viewBox="0 0 200 240" aria-hidden="true">
    <g stroke={stroke} strokeWidth="9" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M40 120 L54 178 L100 210 L146 178 L160 120" />
      <path d="M48 78 L84 38 L116 38 L152 78" />
      <path d="M40 120 L48 78 M160 120 L152 78" />
    </g>
    <g fill={stroke}>
      <circle cx="72" cy="110" r="9" />
      <circle cx="128" cy="110" r="9" />
      <circle cx="100" cy="160" r="9" />
    </g>
  </svg>
);

// ── VCLockGlyph — privacy padlock (replaces the emoji flow-wide) ────────────
export const VCLockGlyph = ({ size = 22 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 28"
    aria-hidden="true"
    style={{ width: size, height: size * (28 / 24), flex: 'none' }}
  >
    <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <path d="M7 11 V8 a5 5 0 0 1 10 0 v3" />
      <rect x="4" y="11" width="16" height="13" rx="4" />
    </g>
    <circle cx="12" cy="17.5" r="2.2" fill="currentColor" />
  </svg>
);

// ── VCCheckGlyph — designed check (CTA seals, pills, valid fields) ──────────
export const VCCheckGlyph = ({ size = 16, strokeWidth = 2.5 }: { size?: number; strokeWidth?: number }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ width: size, height: size, flex: 'none' }}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ── G2FaceMesh — the chamber landmark mesh (signature motif) ────────────────
// Exported so the live-selfie alignment (faceAlign) can derive the canonical
// eye centers from the SAME source of truth, never hardcoded.
export const G2_PTS: Record<string, Array<[number, number]>> = {
  jaw: [[40, 120], [44, 150], [54, 178], [72, 200], [100, 210], [128, 200], [146, 178], [156, 150], [160, 120]],
  brow: [[62, 52], [84, 38], [100, 34], [116, 38], [138, 52]],
  temples: [[48, 78], [152, 78]],
  browL: [[62, 96], [76, 90], [90, 94]],
  browR: [[110, 94], [124, 90], [138, 96]],
  eyeL: [[66, 110], [78, 106], [90, 110], [78, 114]],
  eyeR: [[110, 110], [122, 106], [134, 110], [122, 114]],
  nose: [[100, 108], [100, 128], [92, 142], [100, 146], [108, 142]],
  lips: [[80, 172], [100, 166], [120, 172], [100, 180]],
  cheeks: [[58, 140], [142, 140]],
  chin: [[100, 196]],
};

function g2Path(pts: Array<[number, number]>, close?: boolean): string {
  return pts.map((p, i) => (i ? 'L' : 'M') + p[0] + ' ' + p[1]).join(' ') + (close ? ' Z' : '');
}

interface G2FaceMeshProps {
  stroke?: string;
  dot?: string;
  strokeWidth?: number;
  dotR?: number;
  meshOpacity?: number;
  dotOpacity?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const G2FaceMesh = ({
  stroke = 'var(--mesh-stroke)',
  dot = 'var(--mesh-dot)',
  strokeWidth = 0.8,
  dotR = 1.8,
  meshOpacity = 0.5,
  dotOpacity = 1,
  className,
  style,
}: G2FaceMeshProps) => {
  const P = G2_PTS;
  const lines = [
    g2Path(P.jaw),
    g2Path([P.temples[0], ...P.brow, P.temples[1]]),
    g2Path([P.jaw[0], P.temples[0]]),
    g2Path([P.jaw[8], P.temples[1]]),
    g2Path(P.browL),
    g2Path(P.browR),
    g2Path(P.eyeL, true),
    g2Path(P.eyeR, true),
    g2Path(P.nose),
    g2Path(P.lips, true),
    g2Path([P.cheeks[0], P.nose[2]]),
    g2Path([P.cheeks[1], P.nose[4]]),
    g2Path([P.cheeks[0], P.eyeL[3]]),
    g2Path([P.cheeks[1], P.eyeR[3]]),
    g2Path([P.cheeks[0], P.lips[0]]),
    g2Path([P.cheeks[1], P.lips[2]]),
    g2Path([P.lips[3], P.chin[0]]),
    g2Path([P.nose[1], P.eyeL[2]]),
    g2Path([P.nose[1], P.eyeR[0]]),
  ];
  const dots = Object.values(P).flat();
  return (
    <svg viewBox="0 0 200 240" className={className} style={style} aria-hidden="true">
      <g stroke={stroke} strokeWidth={strokeWidth} fill="none" opacity={meshOpacity} strokeLinecap="round" strokeLinejoin="round">
        {lines.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
      <g fill={dot} opacity={dotOpacity}>
        {dots.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r={dotR} />
        ))}
      </g>
    </svg>
  );
};

// ── VCScanFaceGlyph — launcher end-cap mark (face-scan frame) ───────────────
// Two motifs the register already owns: the photo-step corner-bracket scan
// frame + a minimal face whose landmarks are DERIVED from the canonical G2_PTS
// (brows, eye marks, nose tick, mouth arc) — same source of truth as
// G2FaceMesh, never redrawn with new coordinates. Shares the 200×240 viewBox.
const scanFaceCentroid = (pts: Array<[number, number]>): [number, number] => {
  const n = pts.length;
  return [
    pts.reduce((s, p) => s + p[0], 0) / n,
    pts.reduce((s, p) => s + p[1], 0) / n,
  ];
};

interface VCScanFaceGlyphProps {
  stroke?: string;
  frameStroke?: number;
  faceStroke?: number;
}

export const VCScanFaceGlyph = ({
  stroke = 'var(--launcher-glyph)',
  frameStroke = 13,
  faceStroke = 9,
}: VCScanFaceGlyphProps) => {
  const P = G2_PTS;
  const [elx, ely] = scanFaceCentroid(P.eyeL);
  const [erx, ery] = scanFaceCentroid(P.eyeR);
  const brackets = [
    'M28 60 V44 Q28 28 44 28 H60',
    'M140 28 H156 Q172 28 172 44 V60',
    'M172 180 V196 Q172 212 156 212 H140',
    'M60 212 H44 Q28 212 28 196 V180',
  ];
  const face = [
    g2Path(P.browL),
    g2Path(P.browR),
    `M${elx - 9} ${ely} h18`,
    `M${erx - 9} ${ery} h18`,
    `M${P.nose[0][0]} ${P.nose[0][1] + 6} V${P.nose[3][1] - 2}`,
    `M${P.lips[0][0]} ${P.lips[0][1]} Q${P.lips[3][0]} ${P.lips[3][1] + 6} ${P.lips[2][0]} ${P.lips[2][1]}`,
  ];
  return (
    <svg viewBox="0 0 200 240" aria-hidden="true">
      <g fill="none" stroke={stroke} strokeLinecap="round" strokeLinejoin="round">
        <g strokeWidth={frameStroke}>
          {brackets.map((d, i) => (
            <path key={`b${i}`} d={d} />
          ))}
        </g>
        <g strokeWidth={faceStroke}>
          {face.map((d, i) => (
            <path key={`f${i}`} d={d} />
          ))}
        </g>
      </g>
    </svg>
  );
};

// ── VCConstellation — the profile constellation (consent seed) ──────────────
// Same landmark coordinates as G2FaceMesh so the accumulated profile
// resolves into the chamber mesh. stage = how many groups are lit
// (1 = consent seed … 8 = complete).
const VC_CONST_GROUPS: Array<{
  k: string;
  pts: Array<[number, number]>;
  edges: Array<[[number, number], [number, number]]>;
}> = [
  { k: 'seed', pts: [[48, 78], [152, 78]], edges: [] },
  { k: 'age', pts: [[100, 34]], edges: [[[48, 78], [100, 34]], [[152, 78], [100, 34]]] },
  { k: 'type', pts: [[58, 140], [142, 140]], edges: [[[48, 78], [58, 140]], [[152, 78], [142, 140]]] },
  { k: 'concerns', pts: [[54, 178], [146, 178]], edges: [[[58, 140], [54, 178]], [[142, 140], [146, 178]]] },
  { k: 'q1', pts: [[78, 106], [122, 106]], edges: [[[48, 78], [78, 106]], [[152, 78], [122, 106]]] },
  { k: 'q2', pts: [[100, 146]], edges: [[[78, 106], [100, 146]], [[122, 106], [100, 146]], [[58, 140], [100, 146]], [[142, 140], [100, 146]]] },
  { k: 'q3', pts: [[80, 172], [120, 172]], edges: [[[100, 146], [80, 172]], [[100, 146], [120, 172]]] },
  { k: 'q4', pts: [[100, 196]], edges: [[[80, 172], [100, 196]], [[120, 172], [100, 196]], [[54, 178], [100, 196]], [[146, 178], [100, 196]]] },
];

interface VCConstellationProps {
  stage?: number;
  size?: number;
  dim?: boolean;
  oval?: boolean;
  complete?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export const VCConstellation = ({
  stage = 8,
  size = 96,
  dim = false,
  oval = true,
  complete,
  style,
  className,
}: VCConstellationProps) => {
  const isComplete = complete != null ? complete : stage >= 8;
  const node = 'var(--mesh-dot)';
  const edge = 'var(--mesh-stroke)';
  const groups = VC_CONST_GROUPS.slice(0, Math.max(1, Math.min(8, stage)));
  const lastIdx = groups.length - 1;
  return (
    <svg
      viewBox="24 14 152 212"
      aria-hidden="true"
      className={className}
      style={{ width: size, height: size * (212 / 152), opacity: dim ? 0.55 : 1, ...style }}
    >
      {oval ? (
        <ellipse
          cx="100" cy="122" rx="66" ry="98" fill="none"
          stroke={edge} strokeWidth="1" strokeDasharray="2.5 6.5"
          opacity={isComplete ? 0.55 : 0.3} strokeLinecap="round"
        />
      ) : null}
      <g stroke={edge} strokeWidth="0.9" strokeLinecap="round" opacity={0.55}>
        {groups.flatMap((g, gi) =>
          g.edges.map((e, ei) => (
            <line
              key={g.k + ei}
              x1={e[0][0]} y1={e[0][1]} x2={e[1][0]} y2={e[1][1]}
              opacity={gi === lastIdx && !isComplete ? 1 : 0.7}
            />
          )),
        )}
      </g>
      <g>
        {groups.flatMap((g, gi) => {
          const lit = gi === lastIdx || isComplete;
          return g.pts.map((p, pi) => (
            <g key={g.k + pi}>
              {lit ? <circle cx={p[0]} cy={p[1]} r="7.5" fill={node} opacity="0.18" /> : null}
              <circle cx={p[0]} cy={p[1]} r={lit ? 3.4 : 2.6} fill={node} opacity={lit ? 1 : 0.65} />
            </g>
          ));
        })}
      </g>
    </svg>
  );
};
